import React, { useState, useEffect } from 'react';
import HeaderRow from '../header/row/HeaderRow';
import BodyRow from '../body/row/BodyRow';

/**
 * @typedef {Object} RowData
 * @property {number|string} id - Unique identifier for the row
 * @property {Array<string|React.ReactNode>} contents - Array of cell contents for the row
 * @property {Array<React.ReactNode>} [swappableComponents=[]] - Array of components that can be swapped into cells
 * @property {Array<number>} [swappedIndices=[]] - Array of indices where swappable components should be shown
 * @property {boolean} [expanded=false] - Whether the row is expanded
 * @property {boolean} [checked=false] - Whether the row is checked
 * @property {boolean} [isExpandable=true] - Whether the row can be expanded
 * @property {boolean} [hasCheckbox=true] - Whether the row has a checkbox
 * @property {React.ReactNode} [expandedContent=null] - Content to show when row is expanded
 */

/**
 * @typedef {Object} TableProps
 * @property {Array<string>} [headers] - Table headers
 * @property {Array<RowData>} [data] - Array of row data objects
 * @property {Function} [onRowExpandToggle] - Callback when a row's expand state changes (rowId, isExpanded)
 * @property {Function} [onRowCheckToggle] - Callback when a row's checkbox state changes (rowId, isChecked)
 * @property {Function} [onHeaderCheckToggle] - Callback when header checkbox changes
 * @property {'XL'|'L'|'M'|'S'} [size='M'] - Size of the table rows
 * @property {string} [className=""] - Additional CSS classes for the table
 * @property {Function} [filterByColumn] - Filter function for filtering rows by column value
 */

/**
 * Table Component with expandable rows and checkbox functionality
 * 
 * @param {TableProps} props - Table component props
 * @returns {React.ReactElement} Table component
 */
const Table = ({
  headers = ['Header 1', 'Header 2', 'Header 3', 'Header 4'],
  data = [],
  onRowExpandToggle = null,
  onRowCheckToggle = null,
  onHeaderCheckToggle = null,
  size = 'M',
  className = "",
  filterByColumn = null,
}) => {
  // State for rows data and header checkbox
  const [rowsData, setRowsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [headerChecked, setHeaderChecked] = useState(false);
  const [hasExpandableRows, setHasExpandableRows] = useState(false);
  const [hasCheckboxRows, setHasCheckboxRows] = useState(false);

  // Initialize and normalize row data
  useEffect(() => {
    if (data.length > 0) {
      const normalizedData = data.map(row => ({
        ...row,
        expanded: row.expanded || false,
        checked: row.checked || false,
        isExpandable: row.isExpandable !== undefined ? row.isExpandable : true,
        hasCheckbox: row.hasCheckbox !== undefined ? row.hasCheckbox : true,
        swappableComponents: row.swappableComponents || [],
        swappedIndices: row.swappedIndices || [],
      }));
      
      setRowsData(normalizedData);
      setFilteredData(normalizedData);
    } else {
      setRowsData([]);
      setFilteredData([]);
    }
  }, [data]);

  // Apply filter if provided
  useEffect(() => {
    if (filterByColumn && typeof filterByColumn === 'function') {
      setFilteredData(rowsData.filter(filterByColumn));
    } else {
      setFilteredData(rowsData);
    }
  }, [rowsData, filterByColumn]);

  // Determine if any rows have expandable content or checkboxes
  useEffect(() => {
    setHasExpandableRows(filteredData.some(row => row.isExpandable));
    setHasCheckboxRows(filteredData.some(row => row.hasCheckbox));
  }, [filteredData]);

  // Update header checkbox state based on all checkable rows
  useEffect(() => {
    const checkableRows = filteredData.filter(row => row.hasCheckbox);
    if (checkableRows.length === 0) {
      setHeaderChecked(false);
      return;
    }
    
    const allChecked = checkableRows.every(row => row.checked);
    setHeaderChecked(allChecked);
  }, [filteredData]);

  // Handle row expansion toggle
  const handleExpandToggle = (rowId, isExpanded) => {
    setRowsData(prevRows => 
      prevRows.map(row => 
        row.id === rowId ? { ...row, expanded: isExpanded } : row
      )
    );
    
    if (onRowExpandToggle) {
      onRowExpandToggle(rowId, isExpanded);
    }
  };

  // Handle row checkbox toggle
  const handleCheckToggle = (rowId, isChecked) => {
    setRowsData(prevRows => 
      prevRows.map(row => 
        row.id === rowId ? { ...row, checked: isChecked } : row
      )
    );
    
    if (onRowCheckToggle) {
      onRowCheckToggle(rowId, isChecked);
    }
  };

  // Handle header checkbox toggle
  const handleHeaderCheckToggle = (isChecked) => {
    setHeaderChecked(isChecked);
    
    setRowsData(prevRows => 
      prevRows.map(row => ({ 
        ...row, 
        checked: row.hasCheckbox ? isChecked : row.checked 
      }))
    );
    
    if (onHeaderCheckToggle) {
      onHeaderCheckToggle(isChecked);
    }
  };

  return (
    <div className={`overflow-hidden border border-gray-200 rounded-md ${className} `}>
      {/* Table Header using HeaderRow component */}
      <HeaderRow 
        type={hasCheckboxRows ? 'Checkbox' : 'Normal'}
        headers={headers}
        size={size}
        initialChecked={headerChecked}
        onCheckToggle={handleHeaderCheckToggle}
        showExpandableSpace={hasExpandableRows}
        showCheckboxSpace={hasCheckboxRows}
        hasCheckbox={hasCheckboxRows}
      />
      
      {/* Table Body using BodyRow components */}
      <div className="bg-white divide-y divide-gray-200" style={{ position: "relative", zIndex: 1 }}>
        {filteredData.map((row, rowIndex) => (
          <BodyRow
            key={row.id || rowIndex}
            type={determineRowType(row)}
            contents={row.contents}
            swappableComponents={row.swappableComponents || []}
            swappedIndices={row.swappedIndices || []}
            size={size}
            initialExpanded={row.expanded}
            initialChecked={row.checked}
            onExpandToggle={(isExpanded) => handleExpandToggle(row.id, isExpanded)}
            onCheckToggle={(isChecked) => handleCheckToggle(row.id, isChecked)}
            expandedContent={row.expandedContent}
            className={row.checked ? '' : ''}
            showExpandableSpace={hasExpandableRows && !row.isExpandable}
            showCheckboxSpace={hasCheckboxRows && !row.hasCheckbox}
            isExpandable={row.isExpandable}
            hasCheckbox={row.hasCheckbox}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Helper function to determine the type of row based on its properties
 * 
 * @param {RowData} row - The row data
 * @returns {string} The row type for BodyRow component
 */
const determineRowType = (row) => {
  if (row.isExpandable && row.hasCheckbox) {
    return 'CheckboxExpandable';
  } else if (row.isExpandable && !row.hasCheckbox) {
    return 'Expandable';
  } else if (!row.isExpandable && row.hasCheckbox) {
    return 'Checkbox';
  } else {
    return 'Normal';
  }
};

export default Table;